import { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { depascalize, SortOrder, listFiltersService, ListTableSort, DropdownOption } from "sprint-common-web";

import { ContentSource } from "../../../common/types/ContentSource";

export const sourceOptions = [ContentSource.Sprint, ContentSource.Buzz].map((source) => ({
    key: source,
    text: depascalize(ContentSource[source])
}));

export interface AncillaryListingFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    title?: string;
    includeMerged?: boolean;
    brandings?: string[];
    versionTypes?: string[];
    sources?: ContentSource[];
}

const defaultFilters: AncillaryListingFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "title",
    sortOrder: "asc",
    title: "",
    includeMerged: false,
    brandings: undefined,
    versionTypes: undefined,
    sources: undefined
};

export const useAncillaryListingFilters = () => {
    const filtersService = useMemo(() => listFiltersService<AncillaryListingFilters>(defaultFilters), []);
    const location = useLocation();
    const history = useHistory();

    const [filters, setFilters] = useState<AncillaryListingFilters>(() =>
        filtersService.getSavedFilters(location.search)
    );

    const setSort = (sort: ListTableSort) => {
        saveAndSetFilters({ sortBy: sort.by, sortOrder: sort.order, page: 1 });
    };

    const setPage = (page: number) => {
        saveAndSetFilters({ page });
    };

    const setPageSize = (pageSize: number): void => {
        saveAndSetFilters({ pageSize, page: 1 });
    };

    const setTitle = (title?: string): void => {
        saveAndSetFilters({ title });
    };

    const setIncludeMerged = (includeMerged?: boolean): void => {
        saveAndSetFilters({ includeMerged });
    };

    const setBrandings = (airlines: []): void => {
        const brandings = airlines.map((airline: DropdownOption) => airline.key);
        saveAndSetFilters({ brandings });
    };

    const setVersionTypes = (versions: []): void => {
        const versionTypes = versions.map((versionType: DropdownOption) => versionType.key);
        saveAndSetFilters({ versionTypes });
    };

    const setSources = (sources?: ContentSource[]): void => {
        saveAndSetFilters({ sources, page: 1 });
    };

    const clearFilters = (): void => {
        saveAndSetFilters(defaultFilters);
    };

    const saveAndSetFilters = (newFilters: Partial<AncillaryListingFilters>): void => {
        setFilters((prevFilters: AncillaryListingFilters) => {
            const updatedFilters = { ...prevFilters, ...newFilters };

            filtersService.saveFilters(location.pathname, history, updatedFilters);

            return updatedFilters;
        });
    };

    return {
        filters,
        setSort,
        setPage,
        setPageSize,
        setTitle,
        clearFilters,
        setIncludeMerged,
        setBrandings,
        setVersionTypes,
        setSources
    };
};
